// src/pages/OssList.jsx
import { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid2 as Grid, Card, CardContent, Chip, Typography,
  Skeleton, Button,
} from '@mui/material';
import { fetchOssModels } from '../lib/api';

const CATEGORIES = ['ALL', 'llm', 'image', 'video', 'tts'];
const LIMIT = 20;

const fmt = (n) => {
  if (n == null) return null;
  if (n >= 1e12) return (n / 1e12).toFixed(1) + 'T';
  if (n >= 1e9)  return (n / 1e9).toFixed(1)  + 'B';
  if (n >= 1e6)  return (n / 1e6).toFixed(1)  + 'M';
  if (n >= 1e3)  return (n / 1e3).toFixed(1)  + 'k';
  return String(n);
};

const fmtBytes = (b) => b ? (b / 1e9).toFixed(1) + ' GB' : null;

const mono = { fontFamily: '"Courier New", Courier, monospace' };

const ModelCard = ({ model }) => {
  const modelName = model.ID?.split('/').pop() ?? model.ID;
  const author    = model.AUTHOR ?? model.ID?.split('/')[0];

  return (
    <Card
      elevation={0}
      sx={{ bgcolor: '#e4e0da', border: '1px solid #ccc8c2', borderRadius: 1, height: '100%' }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>

        {/* category / family / gated */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
          {model.MODELCATEGORY && (
            <Chip label={model.MODELCATEGORY} size="small"
              sx={{ ...mono, bgcolor: '#ea580c', color: '#fff', height: 18, fontSize: 10 }} />
          )}
          {model.ROOTFAMILY && (
            <Chip label={model.ROOTFAMILY} size="small" variant="outlined"
              sx={{ ...mono, height: 18, fontSize: 10, borderColor: '#ccc8c2', color: '#78716c' }} />
          )}
          {model.GATED === 1 && (
            <Chip label="gated" size="small"
              sx={{ ...mono, bgcolor: '#fef3c7', color: '#92400e', height: 18, fontSize: 10 }} />
          )}
        </Box>

        {/* model name → HuggingFace link */}
        <Typography
          component="a"
          href={`https://huggingface.co/${model.ID}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            ...mono, fontSize: 13, fontWeight: 600, color: '#1c1916',
            textDecoration: 'none', display: 'block', wordBreak: 'break-all',
            mb: 0.25,
            '&:hover': { color: '#ea580c' },
          }}
        >
          {modelName}
        </Typography>

        <Typography sx={{ ...mono, fontSize: 11, color: '#78716c', mb: 1 }}>
          by {author}
        </Typography>

        {/* stats */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
          {model.LIKES    != null && <Typography sx={{ ...mono, fontSize: 11, color: '#78716c' }}>♥ {fmt(model.LIKES)}</Typography>}
          {model.DOWNLOADS != null && <Typography sx={{ ...mono, fontSize: 11, color: '#78716c' }}>↓ {fmt(model.DOWNLOADS)}</Typography>}
          {model.NUM_PARAMETERS && <Typography sx={{ ...mono, fontSize: 11, color: '#78716c' }}>{fmt(model.NUM_PARAMETERS)} params</Typography>}
          {model.USEDSTORAGE    && <Typography sx={{ ...mono, fontSize: 11, color: '#78716c' }}>{fmtBytes(model.USEDSTORAGE)}</Typography>}
        </Box>

        {/* license + format badges */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {model.LICENSE && (
            <Chip label={model.LICENSE} size="small" variant="outlined"
              sx={{ ...mono, height: 16, fontSize: 10, borderColor: '#ccc8c2', color: '#78716c' }} />
          )}
          {model.HAS_GGUF        === 1 && <Chip label="GGUF"        size="small" sx={{ ...mono, bgcolor: '#d1fae5', color: '#065f46', height: 16, fontSize: 10 }} />}
          {model.HAS_ONNX        === 1 && <Chip label="ONNX"        size="small" sx={{ ...mono, bgcolor: '#dbeafe', color: '#1e40af', height: 16, fontSize: 10 }} />}
          {model.HAS_SAFETENSORS === 1 && <Chip label="safetensors" size="small" sx={{ ...mono, bgcolor: '#f3e8ff', color: '#6b21a8', height: 16, fontSize: 10 }} />}
          {model.HAS_MLX         === 1 && <Chip label="MLX"         size="small" sx={{ ...mono, bgcolor: '#fef9c3', color: '#713f12', height: 16, fontSize: 10 }} />}
        </Box>

      </CardContent>
    </Card>
  );
};

const OssList = () => {
  const [models,   setModels]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [page,     setPage]     = useState(0);
  const [hasMore,  setHasMore]  = useState(true);
  const [category, setCategory] = useState('ALL');

  const load = useCallback(async (pageNum, cat, reset) => {
    setLoading(true);
    try {
      const data = await fetchOssModels(pageNum, LIMIT, cat === 'ALL' ? null : cat);
      setModels(prev => reset ? data : [...prev, ...data]);
      setHasMore(data.length === LIMIT);
    } catch (e) {
      console.error('fetchOssModels:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(0);
    load(0, category, true);
  }, [category, load]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next, category, false);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2, py: 3 }}>

      <Typography sx={{ ...mono, fontSize: 16, fontWeight: 700, mb: 0.5 }}>
        OSS Model Explorer
      </Typography>
      <Typography sx={{ ...mono, fontSize: 11, color: '#78716c', mb: 2 }}>
        HuggingFace trending — sorted by likes
      </Typography>

      {/* category filter */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
        {CATEGORIES.map(cat => (
          <Chip
            key={cat}
            label={cat}
            onClick={() => setCategory(cat)}
            size="small"
            sx={{
              ...mono, cursor: 'pointer',
              bgcolor:     category === cat ? '#ea580c' : 'transparent',
              color:       category === cat ? '#fff'    : '#78716c',
              border:      '1px solid',
              borderColor: category === cat ? '#ea580c' : '#ccc8c2',
            }}
          />
        ))}
      </Box>

      {/* cards */}
      <Grid container spacing={1.5}>
        {models.map(m => (
          <Grid key={m.ID} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <ModelCard model={m} />
          </Grid>
        ))}
        {loading && Array.from({ length: 8 }).map((_, i) => (
          <Grid key={`sk-${i}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Skeleton variant="rectangular" height={148}
              sx={{ borderRadius: 1, bgcolor: '#e4e0da' }} />
          </Grid>
        ))}
      </Grid>

      {/* load more */}
      {!loading && hasMore && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            onClick={handleLoadMore}
            variant="outlined"
            size="small"
            sx={{ ...mono, borderColor: '#ccc8c2', color: '#78716c', '&:hover': { borderColor: '#ea580c', color: '#ea580c' } }}
          >
            load more
          </Button>
        </Box>
      )}

      {!loading && models.length === 0 && (
        <Typography sx={{ ...mono, fontSize: 12, color: '#78716c', textAlign: 'center', mt: 4 }}>
          no models found
        </Typography>
      )}

    </Box>
  );
};

export default OssList;
